import { computed } from '@angular/core';
import type { FilterOption, FilterType } from '@indutrust/shared-model';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import {
  addEntity,
  type EntityId,
  removeAllEntities,
  removeEntity,
  updateAllEntities,
  updateEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { injectDispatch, on, withReducer } from '@ngrx/signals/events';
import { filterEvents, type FilterSnapshot } from './filter.events';

/**
 * Expands a simple-mode selection so any group with at least one selected leaf
 * includes every leaf under that group. Flat options without children are kept
 * as-is when selected.
 */
export function expandPartialGroupsToLeaves(
  data: FilterOption[],
  selectedValues: EntityId[]
): EntityId[] {
  return data.flatMap((group) => {
    if (group.children && group.children.length > 0) {
      const anySelected = group.children.some((child) =>
        selectedValues.includes(child.id)
      );
      return anySelected ? group.children.map((child) => child.id) : [];
    }
    return selectedValues.includes(group.id) ? [group.id] : [];
  });
}

/**
 * Signal store handling UI filters and selections for querying resources.
 *
 * Manages a collection of {@link FilterType} entities and exposes methods to mutate
 * filters, selections, and to build API query parameters.
 *
 * @example
 * const filters = inject(FilterStore);
 * filters.addFilter({ id: 'country', data: [], selectedValues: [], mode: 'simple', loading: false, canClear: true });
 */
export const FilterStore = signalStore(
  withEntities<FilterType>(),
  withComputed(({ entities }) => ({
    isLoading: computed(() => entities().some((filter) => filter.loading)),
    selectedCountryId: computed(() =>
      Number(entities().find((f) => f.id === 'country')?.selectedValues)
    ),
  })),
  withReducer(
    on(filterEvents.filterAdded, ({ payload }) =>
      addEntity<FilterType>(payload)
    ),

    on(filterEvents.filterRemoved, ({ payload: filterId }) =>
      removeEntity(filterId)
    ),

    on(filterEvents.allFiltersRemoved, () => removeAllEntities()),

    on(filterEvents.filterDataUpdated, ({ payload: { filterId, data } }) =>
      updateEntity<FilterType>({
        id: filterId,
        changes: { data, loading: false },
      })
    ),

    on(filterEvents.filterModeToggled, ({ payload: filterId }, state) => {
      const filter = state.entityMap[filterId];
      const newMode = filter.mode === 'simple' ? 'advanced' : 'simple';
      return updateEntity<FilterType>({
        id: filterId,
        changes:
          newMode === 'simple'
            ? {
                mode: newMode,
                selectedValues: expandPartialGroupsToLeaves(
                  filter.data,
                  filter.selectedValues
                ),
              }
            : { mode: newMode },
      });
    }),

    on(filterEvents.filterModeSet, ({ payload: { filterId, mode } }, state) => {
      const filter = state.entityMap[filterId];
      return updateEntity<FilterType>({
        id: filterId,
        changes:
          mode === 'simple'
            ? {
                mode,
                selectedValues: expandPartialGroupsToLeaves(
                  filter.data,
                  filter.selectedValues
                ),
              }
            : { mode },
      });
    }),

    on(
      filterEvents.selectionUpdated,
      ({ payload: { filterId, selectedIds } }, state) => {
        const filter = state.entityMap[filterId];

        let newSelectedValues: EntityId[] = [];
        if (filter.mode === 'simple') {
          newSelectedValues = selectedIds.flatMap((groupId) => {
            const group = filter.data.find((g) => g.id === groupId);
            return group?.children
              ? group.children.map((child) => child.id)
              : [groupId];
          });
        } else {
          newSelectedValues = selectedIds;
        }

        return updateEntity<FilterType>({
          id: filterId,
          changes: { selectedValues: newSelectedValues },
        });
      }
    ),

    on(filterEvents.allSelectionsCleared, (_, state) => [
      updateEntities<FilterType>({
        changes: { loading: false, selectedValues: [] },
        ids: Object.values(state.entityMap)
          .filter((filter) => filter.canClear)
          .map((filter) => filter.id),
      }),
      updateAllEntities<FilterType>({ loading: false }),
    ]),

    on(filterEvents.selectionsRestored, ({ payload: snapshots }) =>
      updateEntities<FilterType>({
        changes: (entity) => {
          const snapshot = snapshots.find((s) => s.id === entity.id);
          return {
            mode: snapshot?.mode ?? entity.mode,
            selectedValues: snapshot?.selectedValues ?? [],
          };
        },
        ids: snapshots.map((s) => s.id),
      })
    ),

    on(filterEvents.allLoadingUpdated, ({ payload: loading }) =>
      updateAllEntities<FilterType>({ loading })
    )
  ),
  withMethods(() => {
    const dispatch = injectDispatch(filterEvents);

    return {
      /**
       * Adds a new {@link FilterType} to the store.
       */
      addFilter(filter: FilterType) {
        dispatch.filterAdded(filter);
      },

      /**
       * Removes a filter by its identifier.
       */
      removeFilter(filterId: EntityId) {
        dispatch.filterRemoved(filterId);
      },

      /**
       * Removes all filters from the store.
       */
      removeAllFilters() {
        dispatch.allFiltersRemoved();
      },

      /**
       * Updates the data options for a specific filter and marks it as not loading.
       */
      updateFilterData(filterId: EntityId, data: FilterOption[]) {
        dispatch.filterDataUpdated({ filterId, data });
      },

      /**
       * Toggles the mode of a filter between 'simple' and 'advanced'.
       * Switching to advanced preserves leaf ids. Switching to simple expands any
       * partially selected groups to all of their leaves (category semantics).
       */
      toggleFilterMode(filterId: EntityId) {
        dispatch.filterModeToggled(filterId);
      },

      /**
       * Explicitly sets the mode of a filter. Switching to simple expands any
       * partially selected groups to all of their leaves; advanced leaves ids as-is.
       */
      setFilterMode(filterId: EntityId, mode: 'simple' | 'advanced') {
        dispatch.filterModeSet({ filterId, mode });
      },

      /**
       * Updates selected values for a filter, supporting simple and advanced modes.
       */
      updateSelection(
        filterId: EntityId,
        selectedIds: FilterType['selectedValues']
      ) {
        dispatch.selectionUpdated({ filterId, selectedIds });
      },

      /**
       * Clears selections on all filters that can be cleared and marks all filters as not loading.
       */
      clearAllSelections() {
        dispatch.allSelectionsCleared();
      },

      /**
       * Sets the loading flag for all filters at once.
       */
      updateAllFiltersLoading(loading: boolean) {
        dispatch.allLoadingUpdated(loading);
      },

      /**
       * Restores filter mode and selections from a snapshot.
       */
      restoreSelections(snapshots: FilterSnapshot[]) {
        dispatch.selectionsRestored(snapshots);
      },
    };
  }),
  withMethods((store) => ({
    /**
     * Returns the currently selected value(s) for a given filter.
     */
    getFilterSelectedValues(filterId: EntityId) {
      return (
        store.entities().find((entry) => entry.id === filterId)
          ?.selectedValues ?? []
      );
    },

    /**
     * Builds a dictionary of API query parameters based on current filter selections.
     */
    buildApiFilterParams(): Record<string, EntityId | EntityId[]> {
      const params: Record<string, EntityId | EntityId[]> = {};

      for (const filter of store.entities()) {
        if (!filter.apiParamKey || filter.selectedValues.length === 0) {
          continue;
        }

        params[filter.apiParamKey] =
          filter.apiParamKey.endsWith('[]') || !filter.multiple
            ? filter.selectedValues[0]
            : filter.selectedValues.join(',');
      }

      return params;
    },
  }))
);
